import React, { useState, useMemo, useEffect } from 'react';
import { useTasks } from '../hooks/useTasks';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { useCustomers } from '../hooks/useCustomers';
import { useUsers } from '../hooks/useUsers';
import { Task, CreateTaskData, TaskStats } from '../types/task';
import { db } from '../lib/firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import CommonPageHeader from '../components/common/CommonPageHeader';
import TaskForm from '../components/tasks/TaskForm';
import TaskList from '../components/tasks/TaskList';
import TaskView from '../components/tasks/TaskView';
import TaskCommentsModal from '../components/tasks/TaskCommentsModal';
import BulkTaskCreator, { BulkTaskData } from '../components/tasks/BulkTaskCreator';
import GlobalTimeTracker from '../components/layout/GlobalTimeTracker';
import SearchableSelect from '../components/common/SearchableSelect';
import { Card, CardContent, CardHeader } from '../components/common/Card';
import LoadingScreen from '../components/common/LoadingScreen';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  UserIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  XMarkIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const Tasks: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const { projects } = useProjects();
  const { customers } = useCustomers();
  const { users } = useUsers();
  const {
    tasks,
    loading,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    updateTaskPriority,
    assignTask,
    unassignTask,
    getTaskStats,
    getMyTasks,
    addComment,
    deleteComment,
    updateComment,
    deleteCommentAttachment,
    exportTaskAsText,
    downloadTasksAsText
  } = useTasks();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkCreatorOpen, setIsBulkCreatorOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [viewingTask, setViewingTask] = useState<Task | undefined>(undefined);
  const [commentingTask, setCommentingTask] = useState<Task | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'all' | 'my' | 'stats'>('all');
  
  // Szűrők
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterCustomer, setFilterCustomer] = useState<string>('');
  const [filterAssignee, setFilterAssignee] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Effect to handle FAB trigger for new task
  useEffect(() => {
    const handleFABNewTask = () => {
      setSelectedTask(undefined);
      setIsFormOpen(true);
    };

    window.addEventListener('openTaskForm', handleFABNewTask);
    return () => {
      window.removeEventListener('openTaskForm', handleFABNewTask);
    };
  }, []);

  // Frissítjük a commentingTask-ot amikor a tasks változik
  useEffect(() => {
    if (commentingTask) {
      const updatedTask = tasks.find(t => t.id === commentingTask.id);
      if (updatedTask) {
        setCommentingTask(updatedTask);
      }
    }
  }, [tasks, commentingTask]);

  // Frissítjük a viewingTask-ot amikor a tasks változik
  useEffect(() => {
    if (viewingTask) {
      const updatedTask = tasks.find(t => t.id === viewingTask.id);
      if (updatedTask) {
        setViewingTask(updatedTask);
      }
    }
  }, [tasks, viewingTask]);

  // Feladat statisztikák - hooks mindig futnak
  const taskStats = useMemo(() => getTaskStats(), [getTaskStats]);
  const myTasks = useMemo(() => getMyTasks(), [getMyTasks]);

  // Szűrt task lista
  const filteredTasks = useMemo(() => {
    let filtered = activeTab === 'my' ? myTasks : tasks;

    if (filterProject) {
      filtered = filtered.filter(task => task.projectId === filterProject);
    }

    if (filterCustomer) {
      filtered = filtered.filter(task => task.customerId === filterCustomer);
    }

    if (filterAssignee) {
      filtered = filtered.filter(task => task.assignedTo === filterAssignee);
    }

    return filtered;
  }, [tasks, myTasks, activeTab, filterProject, filterCustomer, filterAssignee]);

  // Jogosultság ellenőrzés
  if (!hasAccess('tasks')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Feladat létrehozása
  const handleCreateTask = async (data: CreateTaskData) => {
    try {
      await createTask(data);
      toast.success(t('tasks.messages.createSuccess'));
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(t('tasks.messages.createError'));
    }
  };

  // Feladat frissítése
  const handleUpdateTask = async (data: CreateTaskData) => {
    if (!selectedTask) return;
    
    try {
      await updateTask(selectedTask.id, data);
      toast.success(t('tasks.messages.updateSuccess'));
      setSelectedTask(undefined);
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(t('tasks.messages.updateError'));
    }
  };

  // Feladat törlése
  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      toast.success(t('tasks.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(t('tasks.messages.deleteError'));
    }
  };

  // Összes task kommentjeinek exportálása (szűrt lista alapján)
  const handleExportAllComments = () => {
    try {
      // Cím generálása a szűrők alapján
      let title = 'Feladat Kommentek';
      const filters: string[] = [];
      
      if (filterProject) {
        const project = projects.find(p => p.id === filterProject);
        if (project) filters.push(`Projekt: ${project.name}`);
      }
      if (filterCustomer) {
        const customer = customers.find(c => c.id === filterCustomer);
        if (customer) filters.push(`Ügyfél: ${customer.companyName}`);
      }
      if (filterAssignee) {
        const user = users.find(u => u.id === filterAssignee);
        if (user) filters.push(`Felelős: ${user.displayName || user.email}`);
      }
      
      let content = `${title}\n`;
      if (filters.length > 0) {
        content += `Szűrők: ${filters.join(', ')}\n`;
      }
      content += `Exportálva: ${new Date().toLocaleString('hu-HU')}\n`;
      content += `${'='.repeat(80)}\n\n`;

      // Szűrt task lista használata
      const tasksWithComments = filteredTasks.filter(task => task.comments && task.comments.length > 0);
      
      if (tasksWithComments.length === 0) {
        toast.error('Nincsenek kommentek az exportáláshoz');
        return;
      }

      tasksWithComments.forEach((task, index) => {
        content += `${index + 1}. ${task.taskNumber || 'N/A'} - ${task.title}\n`;
        content += `${'-'.repeat(80)}\n`;
        
        if (task.comments && task.comments.length > 0) {
          task.comments.forEach((comment, commentIndex) => {
            const date = comment.createdAt instanceof Date 
              ? comment.createdAt.toLocaleString('hu-HU')
              : new Date(comment.createdAt).toLocaleString('hu-HU');
            
            content += `\n[${commentIndex + 1}] ${comment.userName} - ${date}\n`;
            content += `${comment.message}\n`;
          });
        }
        
        content += `\n${'='.repeat(80)}\n\n`;
      });

      // Fájl letöltése
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Fájlnév generálása szűrők alapján
      let filename = 'task_kommentek';
      if (filterProject) {
        const project = projects.find(p => p.id === filterProject);
        if (project) filename += `_${project.name.replace(/[^a-z0-9]/gi, '_')}`;
      }
      if (filterCustomer) {
        const customer = customers.find(c => c.id === filterCustomer);
        if (customer) filename += `_${customer.companyName.replace(/[^a-z0-9]/gi, '_')}`;
      }
      filename += `_${new Date().toISOString().split('T')[0]}.txt`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const filterInfo = filters.length > 0 ? ` (${filters.join(', ')})` : '';
      toast.success(`${tasksWithComments.length} feladat kommentjei exportálva!${filterInfo}`);
    } catch (error) {
      console.error('Error exporting comments:', error);
      toast.error('Hiba történt az exportálás során');
    }
  };

  // Feladat státusz frissítése
  const handleUpdateStatus = async (taskId: string, status: Task['status']) => {
    try {
      await updateTaskStatus(taskId, status);
      toast.success(t('tasks.messages.statusUpdateSuccess'));
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(t('tasks.messages.statusUpdateError'));
    }
  };

  // Feladat prioritás frissítése
  const handleUpdatePriority = async (taskId: string, priority: Task['priority']) => {
    try {
      await updateTaskPriority(taskId, priority);
      toast.success(t('tasks.messages.priorityUpdateSuccess'));
    } catch (error) {
      console.error('Error updating task priority:', error);
      toast.error(t('tasks.messages.priorityUpdateError'));
    }
  };

  // Feladat hozzárendelése (dropdown-ból)
  const handleAssign = async (taskId: string, userId: string, userName: string) => {
    try {
      await assignTask(taskId, userId, userName);
      toast.success(t('tasks.messages.assignSuccess') || 'Felelős sikeresen hozzárendelve');
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error(t('tasks.messages.assignError') || 'Hiba a felelős hozzárendelésekor');
    }
  };

  // Feladat hozzárendelése saját magunkhoz
  const handleAssignToMe = async (taskId: string) => {
    if (!user) return;
    
    try {
      await assignTask(taskId, user.id, user.displayName);
      toast.success(t('tasks.messages.assignSuccess') || 'Feladat hozzárendelve');
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error(t('tasks.messages.assignError') || 'Hiba a hozzárendeléskor');
    }
  };

  // Feladat hozzárendelés eltávolítása
  const handleUnassign = async (taskId: string) => {
    try {
      await unassignTask(taskId);
      toast.success(t('tasks.messages.unassignSuccess') || 'Felelős eltávolítva');
    } catch (error) {
      console.error('Error unassigning task:', error);
      toast.error(t('tasks.messages.assignError') || 'Hiba az eltávolításkor');
    }
  };

  // Határidő frissítése
  const handleUpdateDueDate = async (taskId: string, dueDate: Date | null) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        dueDate: dueDate || null,
        updatedAt: serverTimestamp()
      });
      toast.success('Határidő frissítve');
    } catch (error) {
      console.error('Error updating due date:', error);
      toast.error('Hiba a határidő frissítésekor');
    }
  };

  // Projekt frissítése
  const handleUpdateProject = async (taskId: string, projectId: string | null) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        projectId: projectId || null,
        updatedAt: serverTimestamp()
      });
      toast.success('Projekt frissítve');
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Hiba a projekt frissítésekor');
    }
  };

  // Feladat szerkesztése
  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setIsFormOpen(true);
  };

  // Feladat megtekintése
  const handleViewTask = (task: Task) => {
    setViewingTask(task);
  };

  // Kommentek megtekintése
  const handleViewComments = (task: Task) => {
    setCommentingTask(task);
  };

  // Form bezárása
  const handleCloseForm = () => {
    setSelectedTask(undefined);
    setIsFormOpen(false);
  };

  // View bezárása
  const handleCloseView = () => {
    setViewingTask(undefined);
  };

  // Comments modal bezárása
  const handleCloseComments = () => {
    setCommentingTask(undefined);
  };

  // Új feladat form
  const handleNewTask = () => {
    setSelectedTask(undefined);
    setIsFormOpen(true);
  };

  // Bulk task creator megnyitása
  const handleOpenBulkCreator = () => {
    setIsBulkCreatorOpen(true);
  };

  // Bulk task létrehozás
  const handleBulkCreateTasks = async (bulkTasks: BulkTaskData[]) => {
    try {
      const createdTasks: string[] = [];
      
      for (const bulkTask of bulkTasks) {
        const taskData: CreateTaskData = {
          title: bulkTask.title,
          description: '',
          priority: bulkTask.priority,
          status: bulkTask.status,
          projectId: bulkTask.projectId,
          customerId: bulkTask.customerId,
          assignedTo: bulkTask.assigneeId,
          assignedToName: bulkTask.assigneeName,
          tags: [],
          documents: []
        };
        
        await createTask(taskData);
        createdTasks.push(bulkTask.title);
      }
      
      toast.success(`${createdTasks.length} feladat sikeresen létrehozva!`);
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      throw error;
    }
  };

  // Komment hozzáadása (ViewTask-hoz)
  const handleAddCommentView = async (message: string, files?: File[]) => {
    if (!viewingTask) return;
    await addComment(viewingTask.id, message, files);
  };

  // Komment törlése (ViewTask-hoz)
  const handleDeleteCommentView = async (commentId: string) => {
    if (!viewingTask) return;
    await deleteComment(viewingTask.id, commentId);
  };

  // Komment frissítése (ViewTask-hoz)
  const handleUpdateCommentView = async (commentId: string, message: string) => {
    if (!viewingTask) return;
    await updateComment(viewingTask.id, commentId, message);
  };

  // Komment melléklet törlése (ViewTask-hoz)
  const handleDeleteAttachmentView = async (commentId: string, attachmentId: string) => {
    if (!viewingTask) return;
    await deleteCommentAttachment(viewingTask.id, commentId, attachmentId);
  };

  // Komment hozzáadása (CommentsModal-hoz)
  const handleAddCommentModal = async (message: string, files?: File[]) => {
    if (!commentingTask) return;
    await addComment(commentingTask.id, message, files);
  };

  // Komment törlése (CommentsModal-hoz)
  const handleDeleteCommentModal = async (commentId: string) => {
    if (!commentingTask) return;
    await deleteComment(commentingTask.id, commentId);
  };

  // Komment frissítése (CommentsModal-hoz)
  const handleUpdateCommentModal = async (commentId: string, message: string) => {
    if (!commentingTask) return;
    await updateComment(commentingTask.id, commentId, message);
  };

  // Komment melléklet törlése (CommentsModal-hoz)
  const handleDeleteAttachmentModal = async (commentId: string, attachmentId: string) => {
    if (!commentingTask) return;
    await deleteCommentAttachment(commentingTask.id, commentId, attachmentId);
  };

  // Feladat exportálása
  const handleExportTask = () => {
    if (!viewingTask) return;
    downloadTasksAsText([viewingTask], `${viewingTask.taskNumber}_${viewingTask.title}.txt`);
    toast.success(t('tasks.messages.exportSuccess') || 'Feladat exportálva');
  };

  // Statisztikák renderelése
  const renderStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Összes feladat */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClipboardDocumentListIcon className="w-8 h-8 text-gray-400" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.totalTasks')}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{taskStats.total}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tennivaló */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="w-8 h-8 text-gray-400" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.todoTasks')}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{taskStats.todo}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Folyamatban */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="w-8 h-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.inProgressTasks')}</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{taskStats.inProgress}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Befejezett */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="w-8 h-8 text-green-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.completedTasks')}</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{taskStats.completed}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lejárt */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.overdueTasks')}</div>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{taskStats.overdue}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ma esedékes */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarIcon className="w-8 h-8 text-orange-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.filters.dueToday')}</div>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{taskStats.dueToday}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Héten esedékes */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarIcon className="w-8 h-8 text-yellow-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.filters.dueThisWeek')}</div>
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{taskStats.dueThisWeek}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Saját feladatok */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="w-8 h-8 text-primary-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.myTasks')}</div>
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{myTasks.length}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Befejezési arány számítása
  const completionRate = taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0;

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Oldal fejléc */}
      <CommonPageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        icon={ClipboardDocumentListIcon}
      >
        <div className="flex items-center space-x-3">
          <GlobalTimeTracker />
          <button
            onClick={handleExportAllComments}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
            title="Összes komment exportálása"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Kommentek</span>
          </button>
          <button
            onClick={handleOpenBulkCreator}
            className="bg-gradient-to-r from-purple-500/30 to-blue-500/30 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:from-purple-500/40 hover:to-blue-500/40 transition-all shadow-lg"
            title="Gyors feladat létrehozás"
          >
            <BoltIcon className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Gyors</span>
          </button>
          <button
            onClick={handleNewTask}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            <span>{t('tasks.create')}</span>
          </button>
        </div>
      </CommonPageHeader>

      {/* Fő tartalom */}
      <div className="space-y-6">
        {/* Navigációs tabok */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'all'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tasks.filters.all')} ({tasks.length})
            </button>
            <button
              onClick={() => setActiveTab('my')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tasks.stats.myTasks')} ({myTasks.length})
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('nav.reports')}
            </button>
          </nav>
        </div>

        {/* Tartalom a kiválasztott tab alapján */}
        {activeTab === 'stats' && renderStats()}
        
        {activeTab === 'stats' && (
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <ChartBarIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('tasks.stats.completionRate')}</h3>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <div className="flex-1">
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <span>{t('tasks.stats.completedTasks')}</span>
                    <span>{completionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-primary-600 dark:bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{taskStats.completed}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">/ {taskStats.total}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Modern szűrő panel */}
        {activeTab !== 'stats' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {/* Szűrő header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <FunnelIcon className="h-5 w-5" />
                    <span className="font-medium">Szűrők</span>
                  </button>
                  
                  {/* Aktív szűrők badge-ek */}
                  <div className="flex items-center space-x-2">
                    {filterProject && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        <BriefcaseIcon className="h-3 w-3 mr-1" />
                        {projects.find(p => p.id === filterProject)?.name}
                        <button
                          onClick={() => setFilterProject('')}
                          className="ml-1 hover:text-blue-900 dark:hover:text-blue-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterCustomer && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                        {customers.find(c => c.id === filterCustomer)?.companyName}
                        <button
                          onClick={() => setFilterCustomer('')}
                          className="ml-1 hover:text-green-900 dark:hover:text-green-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterAssignee && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                        <UserIcon className="h-3 w-3 mr-1" />
                        {users.find(u => u.id === filterAssignee)?.displayName || users.find(u => u.id === filterAssignee)?.email}
                        <button
                          onClick={() => setFilterAssignee('')}
                          className="ml-1 hover:text-purple-900 dark:hover:text-purple-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>

                {/* Összes szűrő törlése */}
                {(filterProject || filterCustomer || filterAssignee) && (
                  <button
                    onClick={() => {
                      setFilterProject('');
                      setFilterCustomer('');
                      setFilterAssignee('');
                    }}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    Összes törlése
                  </button>
                )}
              </div>
            </div>

            {/* Összecsukható szűrő tartalom */}
            {showFilters && (
              <div className="px-4 py-4 bg-gray-50 dark:bg-gray-900/50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Projekt szűrő */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <BriefcaseIcon className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" />
                      Projekt
                    </label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Összes projekt' },
                        ...projects.map(project => ({
                          value: project.id,
                          label: project.name,
                          icon: <BriefcaseIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        }))
                      ]}
                      value={filterProject}
                      onChange={setFilterProject}
                      placeholder="Válassz projektet..."
                      searchPlaceholder="Keresés projektek között..."
                      clearable
                    />
                  </div>

                  {/* Ügyfél szűrő */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <BuildingOfficeIcon className="h-4 w-4 mr-1.5 text-green-600 dark:text-green-400" />
                      Ügyfél
                    </label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Összes ügyfél' },
                        ...customers.map(customer => ({
                          value: customer.id,
                          label: customer.companyName,
                          icon: <BuildingOfficeIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                        }))
                      ]}
                      value={filterCustomer}
                      onChange={setFilterCustomer}
                      placeholder="Válassz ügyfelet..."
                      searchPlaceholder="Keresés ügyfelek között..."
                      clearable
                    />
                  </div>

                  {/* Felelős szűrő */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <UserIcon className="h-4 w-4 mr-1.5 text-purple-600 dark:text-purple-400" />
                      Felelős
                    </label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Összes felelős' },
                        ...users.map(user => ({
                          value: user.id,
                          label: user.displayName || user.email,
                          icon: <UserIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        }))
                      ]}
                      value={filterAssignee}
                      onChange={setFilterAssignee}
                      placeholder="Válassz felelőst..."
                      searchPlaceholder="Keresés felhasználók között..."
                      clearable
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Feladatok listája */}
        {activeTab !== 'stats' && (
          <TaskList
            tasks={filteredTasks}
            loading={loading}
            onEdit={handleEditTask}
            onDelete={handleDeleteTask}
            onUpdateStatus={handleUpdateStatus}
            onUpdatePriority={handleUpdatePriority}
            onAssign={handleAssign}
            onAssignToMe={handleAssignToMe}
            onUnassign={handleUnassign}
            onUpdateDueDate={handleUpdateDueDate}
            onUpdateProject={handleUpdateProject}
            onView={handleViewTask}
            onViewComments={handleViewComments}
          />
        )}
      </div>

      {/* Feladat form modal */}
      {isFormOpen && (
        <TaskForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSubmit={selectedTask ? handleUpdateTask : handleCreateTask}
          initialData={selectedTask}
        />
      )}

      {/* Feladat view modal */}
      {viewingTask && (
        <TaskView
          task={viewingTask}
          isOpen={!!viewingTask}
          onClose={handleCloseView}
          onEdit={handleEditTask}
          onAddComment={handleAddCommentView}
          onDeleteComment={handleDeleteCommentView}
          onUpdateComment={handleUpdateCommentView}
          onDeleteAttachment={handleDeleteAttachmentView}
          onExportTask={handleExportTask}
        />
      )}

      {/* Kommentek modal */}
      {commentingTask && (
        <TaskCommentsModal
          task={commentingTask}
          isOpen={!!commentingTask}
          onClose={handleCloseComments}
          onAddComment={handleAddCommentModal}
          onDeleteComment={handleDeleteCommentModal}
          onUpdateComment={handleUpdateCommentModal}
          onDeleteAttachment={handleDeleteAttachmentModal}
          onExportTask={() => {
            downloadTasksAsText([commentingTask], `${commentingTask.taskNumber}_${commentingTask.title}.txt`);
            toast.success(t('tasks.messages.exportSuccess') || 'Feladat exportálva');
          }}
        />
      )}

      {/* Bulk Task Creator modal */}
      <BulkTaskCreator
        isOpen={isBulkCreatorOpen}
        onClose={() => setIsBulkCreatorOpen(false)}
        onCreateTasks={handleBulkCreateTasks}
      />
    </div>
  );
};

export default Tasks;